import { useMemo, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { HOTELS_QUERY_KEY, listHotels, type Hotel } from '../features/hotels/api.js';
import { HotelFilterDropdown } from '../features/hotels/hotel-filter-dropdown.js';
import { getFavoriteHotels } from '../features/dashboard/api.js';
import {
  listApprovalBatches,
  revokeBatch,
  reissueBatchLink,
  approvalBatchStatus,
  STATUS_LABEL,
  STATUS_PILL_CLASS,
  type ApprovalBatchStatus,
} from '../features/approvals/api.js';
import type { ApprovalBatch, ApprovalStage } from '../features/approvals/types.js';
import { Card } from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';
import { useConfirm } from '../components/ui/confirm-dialog.js';
import { Label } from '../components/ui/label.js';
import { cn } from '../lib/utils.js';

/* ------------------------------------------------------------------ */
/* constants                                                          */
/* ------------------------------------------------------------------ */

const HU_MONTHS = [
  'január',
  'február',
  'március',
  'április',
  'május',
  'június',
  'július',
  'augusztus',
  'szeptember',
  'október',
  'november',
  'december',
];

const STAGE_LABEL: Record<ApprovalStage, string> = {
  topics: 'Témajavaslatok',
  final_content: 'Végső tartalom',
};

const STATUS_ORDER: ApprovalBatchStatus[] = [
  'pending',
  'partial',
  'completed',
  'revoked',
  'expired',
];

type Tab = 'client' | 'date';

/* ------------------------------------------------------------------ */
/* helpers                                                            */
/* ------------------------------------------------------------------ */

function toggle<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' });
}

/** "2026. július" key + label from a createdAt ISO string. */
function monthKey(iso: string): { key: string; label: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { key: iso, label: iso };
  const y = d.getFullYear();
  const m = d.getMonth();
  return { key: `${y}-${String(m).padStart(2, '0')}`, label: `${y}. ${HU_MONTHS[m]}` };
}

/* ------------------------------------------------------------------ */
/* page                                                               */
/* ------------------------------------------------------------------ */

function ApprovalsPage() {
  const qc = useQueryClient();

  const [tab, setTab] = useState<Tab>('client');
  const [statuses, setStatuses] = useState<ApprovalBatchStatus[]>([]);
  const [hotelIds, setHotelIds] = useState<string[]>([]);

  // Shared key from features/hotels/api — every /hotels reader goes through
  // HOTELS_QUERY_KEY so React Query's dedup is the default.
  const hotelsQ = useQuery<Hotel[]>({ queryKey: HOTELS_QUERY_KEY, queryFn: listHotels });
  const favoritesQ = useQuery<string[]>({
    queryKey: ['dashboard-favorites'],
    queryFn: getFavoriteHotels,
  });

  // All org batches; status + hotel filtering happens client-side below.
  const batchesQ = useQuery<ApprovalBatch[]>({
    queryKey: ['approval-batches', 'org'],
    queryFn: () => listApprovalBatches({}),
    staleTime: 15_000,
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeBatch(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['approval-batches'] });
    },
  });

  const allBatches = batchesQ.data ?? [];

  // Client-side multi-status + multi-hotel filtering (server status is single-value).
  const filtered = useMemo(() => {
    return allBatches.filter((b) => {
      if (statuses.length && !statuses.includes(approvalBatchStatus(b))) return false;
      if (hotelIds.length && !hotelIds.includes(b.hotelId)) return false;
      return true;
    });
  }, [allBatches, statuses, hotelIds]);

  const statusCounts = useMemo(() => {
    const m = {} as Record<ApprovalBatchStatus, number>;
    for (const s of STATUS_ORDER) m[s] = 0;
    for (const b of allBatches) m[approvalBatchStatus(b)] += 1;
    return m;
  }, [allBatches]);

  const hasFilters = statuses.length > 0 || hotelIds.length > 0;

  function clearFilters() {
    setStatuses([]);
    setHotelIds([]);
  }

  // Group: by client. The "client" is the HOTEL the agency produces content for
  // — not the recipient email a batch happens to be sent to. So group by hotel.
  const byClient = useMemo(() => {
    const m = new Map<string, { name: string; batches: ApprovalBatch[] }>();
    for (const b of filtered) {
      const entry = m.get(b.hotelId) ?? { name: b.hotelName ?? 'Ismeretlen hotel', batches: [] };
      entry.batches.push(b);
      m.set(b.hotelId, entry);
    }
    return [...m.entries()]
      .map(([hotelId, { name, batches }]) => ({
        hotelId,
        name,
        batches: [...batches].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'hu'));
  }, [filtered]);

  // Group: by createdAt month (newest month first).
  const byMonth = useMemo(() => {
    const m = new Map<string, { label: string; batches: ApprovalBatch[] }>();
    for (const b of filtered) {
      const { key, label } = monthKey(b.createdAt);
      const entry = m.get(key) ?? { label, batches: [] };
      entry.batches.push(b);
      m.set(key, entry);
    }
    return [...m.entries()]
      .map(([key, { label, batches }]) => ({
        key,
        label,
        batches: [...batches].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
      }))
      .sort((a, b) => b.key.localeCompare(a.key));
  }, [filtered]);

  const loading = batchesQ.isLoading;
  const hotels = hotelsQ.data ?? [];

  const header = (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <div className="h2-eyebrow">Ügyfél jóváhagyások</div>
        <h1 className="mt-1.5 text-h1 font-normal leading-none tracking-[-0.02em] text-[var(--ink-700)]">
          Jóváhagyások{' '}
          <span className="font-mono text-h2 font-normal text-[var(--fg-faint)]">
            {allBatches.length}
          </span>
        </h1>
        <p className="mt-2 text-body text-[var(--fg-2)]">
          Minden hotel minden tervének összes jóváhagyási csomagja egy helyen. Új csomagot a terv
          naptár nézetében hozhatsz létre.
        </p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {header}

      {/* Tab switcher — grouping mode */}
      <div className="flex justify-end">
        <div className="inline-flex rounded-pill border border-[var(--app-divider-2)] bg-[var(--app-surface)] p-0.5">
          {(['client', 'date'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              aria-pressed={tab === t}
              className={cn(
                'inline-flex h-8 items-center rounded-pill px-3 text-meta font-medium transition-colors',
                tab === t
                  ? 'bg-[var(--brand-500)] text-white'
                  : 'text-[var(--fg-2)] hover:text-[var(--ink-700)]',
              )}
            >
              {t === 'client' ? 'Ügyfél szerint' : 'Dátum szerint'}
            </button>
          ))}
        </div>
      </div>

      {/* Filter chips */}
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>Státusz</Label>
          <div className="flex flex-wrap gap-1.5">
            {STATUS_ORDER.map((s) => {
              const active = statuses.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatuses((prev) => toggle(prev, s))}
                  aria-pressed={active}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-0.5 text-meta font-medium leading-tight transition-colors',
                    active
                      ? `border-transparent ${STATUS_PILL_CLASS[s]}`
                      : 'border-[var(--app-divider-2)] bg-[var(--app-surface)] text-[var(--fg-muted)] hover:border-[var(--brand-400)] hover:text-[var(--ink-700)]',
                  )}
                >
                  {STATUS_LABEL[s]}
                  <span className="font-mono text-meta text-[var(--fg-faint)]">
                    {statusCounts[s] ?? 0}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Hotel</Label>
          {hotels.length === 0 ? (
            <p className="text-meta text-[var(--fg-faint)]">
              {hotelsQ.isLoading ? 'Hotelek betöltése…' : 'Nincs elérhető hotel.'}
            </p>
          ) : (
            <HotelFilterDropdown
              hotels={hotels}
              favoriteIds={favoritesQ.data ?? []}
              selected={hotelIds}
              onChange={setHotelIds}
            />
          )}
        </div>

        {hasFilters && (
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Szűrők törlése
            </Button>
          </div>
        )}
      </div>

      {loading && <p className="text-body text-[var(--fg-2)]">Betöltés…</p>}

      {batchesQ.error && !loading && (
        <Card className="p-6 text-center text-body text-[var(--danger)]">
          {(batchesQ.error as Error).message}
        </Card>
      )}

      {!loading && !batchesQ.error && filtered.length === 0 && (
        <EmptyState tab={tab} hasFilters={hasFilters} />
      )}

      {/* Ügyfél szerint — az ügyfél a hotel, akinek tartalmat készítünk */}
      {!loading && filtered.length > 0 && tab === 'client' && (
        <div className="space-y-6">
          {byClient.map((group) => (
            <GroupSection key={group.hotelId} title={group.name} count={group.batches.length}>
              {group.batches.map((b) => (
                <BatchRow
                  key={b.id}
                  batch={b}
                  hideHotel
                  onRevoke={() => revoke.mutate(b.id)}
                  revoking={revoke.isPending && revoke.variables === b.id}
                />
              ))}
            </GroupSection>
          ))}
        </div>
      )}

      {/* Dátum szerint */}
      {!loading && filtered.length > 0 && tab === 'date' && (
        <div className="space-y-6">
          {byMonth.map((group) => (
            <GroupSection key={group.key} title={group.label} count={group.batches.length}>
              {group.batches.map((b) => (
                <BatchRow
                  key={b.id}
                  batch={b}
                  onRevoke={() => revoke.mutate(b.id)}
                  revoking={revoke.isPending && revoke.variables === b.id}
                />
              ))}
            </GroupSection>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* collapsible group section                                          */
/* ------------------------------------------------------------------ */

function GroupSection({
  title,
  titleClassName,
  count,
  children,
}: {
  title: string;
  titleClassName?: string;
  count: number;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 border-b border-[var(--app-divider)] pb-2 text-left"
        aria-expanded={open}
      >
        <svg
          width={14}
          height={14}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={cn(
            'shrink-0 text-[var(--fg-faint)] transition-transform',
            open ? 'rotate-90' : '',
          )}
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
        <span className={cn('text-body font-semibold text-[var(--ink-700)]', titleClassName)}>
          {title}
        </span>
        <span className="font-mono text-meta text-[var(--fg-faint)]">{count}</span>
      </button>
      {open && <div className="mt-3 space-y-3">{children}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* batch row                                                          */
/* ------------------------------------------------------------------ */

function BatchRow({
  batch,
  onRevoke,
  revoking,
  hideHotel = false,
}: {
  batch: ApprovalBatch;
  onRevoke: () => void;
  revoking: boolean;
  /** In the hotel-grouped view the hotel name is the section header, so the row
      leads with the stage instead of repeating it. */
  hideHotel?: boolean;
}) {
  const status = approvalBatchStatus(batch);
  const qc = useQueryClient();
  const confirm = useConfirm();
  // Only the hash of a link is stored, so the e-mailed one can't be shown
  // again: the button mints a new one and the old ones stop working. The raw
  // link lives in this state only, until the page is left.
  const [link, setLink] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  async function copy(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 1500);
    } catch {
      setCopyState('failed');
    }
  }

  const reissue = useMutation({
    mutationFn: () => reissueBatchLink(batch.id),
    onSuccess: async (res) => {
      setLink(res.link);
      void qc.invalidateQueries({ queryKey: ['approval-batches'] });
      await copy(res.link);
    },
  });

  async function onCopyLink(): Promise<void> {
    const ok = await confirm({
      title: 'Új ügyfél-link készítése',
      message: [
        'A Hestia a kiküldött linket nem tárolja, ezért új linket készít ehhez a körhöz, és a vágólapra másolja.',
        'A korábban kiküldött link (e-mail, emlékeztető) ettől kezdve nem működik. Az ügyfélnek az új linket küldd el.',
        status === 'expired'
          ? 'Az új link 30 napig érvényes, a lejárt kör ezzel újra megnyílik.'
          : 'Az új link 30 napig érvényes.',
      ].join('\n\n'),
      confirmLabel: 'Új link másolása',
    });
    if (ok) reissue.mutate();
  }

  return (
    <Card className="flex flex-wrap items-center gap-4 p-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-meta font-semibold uppercase tracking-wider',
              STATUS_PILL_CLASS[status],
            )}
          >
            {STATUS_LABEL[status]}
          </span>
          {hideHotel ? (
            <span className="text-body font-semibold text-[var(--ink-700)]">
              {STAGE_LABEL[batch.stage]}
            </span>
          ) : (
            <>
              <span className="text-body font-semibold text-[var(--ink-700)]">
                {batch.hotelName ?? 'Ismeretlen hotel'}
              </span>
              <span className="text-meta text-[var(--fg-2)]">· {STAGE_LABEL[batch.stage]}</span>
            </>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-3 text-meta text-[var(--fg-faint)]">
          <span>
            Címzett: <span className="font-mono">{batch.clientEmail}</span>
          </span>
          <span>
            {batch.decisionCount}/{batch.postCount} eldöntve
          </span>
          <span>Létrehozva {formatDate(batch.createdAt)}</span>
          <span>Lejár {formatDate(batch.expiresAt)}</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Link
          to="/app/approvals/$batchId/preview"
          params={{ batchId: batch.id }}
          title="Pontosan ezt látja az ügyfél a linken"
        >
          <Button size="sm" variant="outline">
            Előnézet
          </Button>
        </Link>
        {status !== 'revoked' && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => void onCopyLink()}
            disabled={reissue.isPending}
            title="Új ügyfél-linket készít, a korábban kiküldött link megszűnik"
          >
            {reissue.isPending ? 'Link készül…' : 'Link másolása'}
          </Button>
        )}
        {status !== 'revoked' && status !== 'expired' && (
          <Button size="sm" variant="ghost" onClick={onRevoke} disabled={revoking}>
            {revoking ? 'Visszavonás…' : 'Visszavonás'}
          </Button>
        )}
      </div>

      {reissue.isError && (
        <p className="basis-full text-meta text-[var(--danger)]">
          Nem sikerült új linket készíteni. Próbáld újra.
        </p>
      )}

      {link && (
        <div className="basis-full space-y-1.5 rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] p-2.5">
          <p className="text-meta font-semibold text-[var(--success)]">
            {copyState === 'failed'
              ? 'Új ügyfél-link — nem sikerült automatikusan másolni, kattints a Másol gombra:'
              : 'Új ügyfél-link, vágólapra másolva:'}
          </p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={link}
              onFocus={(e) => e.currentTarget.select()}
              aria-label="Új ügyfél-link"
              className="h-8 min-w-0 flex-1 rounded-md border border-[var(--app-divider)] bg-[var(--app-surface-2)] px-2 font-mono text-meta text-[var(--ink-700)]"
            />
            <Button size="sm" variant="outline" onClick={() => void copy(link)}>
              {copyState === 'copied' ? 'Másolva' : 'Másol'}
            </Button>
          </div>
          <p className="text-meta text-[var(--fg-faint)]">
            A korábban kiküldött link már nem működik. Ez a link csak most látszik: ha később újra
            kell, készíts újat, az ezt is lezárja.
          </p>
        </div>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* empty state                                                        */
/* ------------------------------------------------------------------ */

function EmptyState({ tab, hasFilters }: { tab: Tab; hasFilters: boolean }) {
  return (
    <Card className="p-8 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[var(--brand-50)] text-[var(--brand-700)]">
        <svg
          width={22}
          height={22}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
      </div>
      <div className="mt-3 text-body font-medium text-[var(--ink-700)]">
        {hasFilters
          ? 'Nincs a szűrőnek megfelelő jóváhagyás'
          : tab === 'client'
            ? 'Még egyetlen ügyfélnek sem küldtél jóváhagyást'
            : 'Még egyetlen jóváhagyási csomag sem készült'}
      </div>
      <p className="mt-1 text-meta text-[var(--fg-2)]">
        {hasFilters
          ? 'Próbáld lazítani a szűrőket vagy törölni a keresést.'
          : 'Új csomag létrehozásához nyiss meg egy havi tervet és görgess a „Jóváhagyási csomag küldése” szekcióhoz.'}
      </p>
    </Card>
  );
}

export const Route = createFileRoute('/app/approvals/')({
  component: ApprovalsPage,
});
